import {
  Baby, HeartHandshake, Milk, Moon, Plane, ToyBrick, BedDouble, ShieldCheck,
  Shirt, Footprints, Package, Circle, Scissors, Smile, Layers, Sparkles,
  Heart, Droplets, Armchair, Leaf, BaggageClaim, Feather, RefreshCw, Square,
  BookOpen, Monitor, Thermometer, Users, CalendarDays, Gift, Ribbon, Bath,
  GraduationCap, CarFront, Notebook,
} from "lucide-react";
import React from "react";

/* ================= TYPES ================= */

export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ReactNode;
  image?: any;                // optional photo — falls back to the icon if omitted
  parentId?: number | null;
  superCategoryId?: number;
  audienceIds?: number[];
}

const sz = 18; // default icon size (px)

/* ═══════════════════════════════════════════════════════════
   AUDIENCE — Baby, Mom & Kids
═══════════════════════════════════════════════════════════ */
export const BabyMaternityAudience: CategoryItem[] = [
  { id: 1, name: "Baby", icon: <Baby size={sz} />, superCategoryId: 2 },
  { id: 2, name: "Mom",  icon: <HeartHandshake size={sz} />, superCategoryId: 2 },
  { id: 3, name: "Kids", icon: <Users size={sz} />, superCategoryId: 2 },
];

/* ═══════════════════════════════════════════════════════════
   AGE GROUPS — 0 months to 10 years
═══════════════════════════════════════════════════════════ */
export const BabyAgeGroups: CategoryItem[] = [
  { id: 41, name: "0-3 Months",  icon: <CalendarDays size={sz} /> },
  { id: 42, name: "3-6 Months",  icon: <CalendarDays size={sz} /> },
  { id: 43, name: "6-12 Months", icon: <CalendarDays size={sz} /> },
  { id: 44, name: "1-2 Years",   icon: <CalendarDays size={sz} /> },
  { id: 45, name: "2-4 Years",   icon: <CalendarDays size={sz} /> },
  { id: 46, name: "4-6 Years",   icon: <CalendarDays size={sz} /> },
  { id: 47, name: "6-10 Years",  icon: <CalendarDays size={sz} /> },
];

/* ═══════════════════════════════════════════════════════════
   DEVELOPMENT MILESTONES — tied to BabyAgeGroups (ageGroupId)
   Informational reference, not a shoppable category.
═══════════════════════════════════════════════════════════ */
export interface DevelopmentMilestone {
  ageGroupId: number;
  ageLabel: string;
  milestones: string[];
}

export const BabyDevelopmentMilestones: DevelopmentMilestone[] = [
  { ageGroupId: 41, ageLabel: "0-3 Months",  milestones: ["Lifts head during tummy time", "Follows objects with eyes", "Begins to smile socially"] },
  { ageGroupId: 42, ageLabel: "3-6 Months",  milestones: ["Rolls over", "Reaches for and grasps objects", "Starts teething"] },
  { ageGroupId: 43, ageLabel: "6-12 Months", milestones: ["Sits without support", "Crawls", "Says first words"] },
  { ageGroupId: 44, ageLabel: "1-2 Years",   milestones: ["Walks independently", "Says short phrases", "Points to objects"] },
  { ageGroupId: 45, ageLabel: "2-4 Years",   milestones: ["Runs and climbs", "Speaks in short sentences", "Begins potty training"] },
  { ageGroupId: 46, ageLabel: "4-6 Years",   milestones: ["Draws basic shapes", "Counts objects", "Dresses independently"] },
  { ageGroupId: 47, ageLabel: "6-10 Years",  milestones: ["Reads independently", "Develops hobbies & interests", "Builds friendships"] },
];

/* ═══════════════════════════════════════════════════════════
   BABY & MATERNITY — TOP-LEVEL SUBCATEGORIES
═══════════════════════════════════════════════════════════ */
export const BabyMaternityCategories: CategoryItem[] = [
  { id: 16, name: "Baby Clothing",       icon: <Baby size={sz} />, image: '/icons/fashion/romper.png' },
  { id: 17, name: "Baby Shoes",          icon: <Footprints size={sz} />, image: '/icons/fashion/shoes.png' },
  { id: 18, name: "Baby Care",           icon: <Baby size={sz} />, image: '/icons/fashion/carrier.png' },
  { id: 19, name: "Mommy Care",          icon: <HeartHandshake size={sz} />, image: '/icons/fashion/mom.png' },
  { id: 20, name: "Baby Feeding",        icon: <Milk size={sz} />, image: '/icons/fashion/feedingbottle.png' },
  { id: 21, name: "Baby Bedtime",        icon: <Moon size={sz} />, image: '/icons/fashion/cradle.png' },
  { id: 22, name: "Baby Travel",         icon: <Plane size={sz} />, image: '/icons/fashion/stroller.png' },
  { id: 23, name: "Baby Toys",           icon: <ToyBrick size={sz} />, image: '/icons/fashion/toys.png' },
  { id: 24, name: "Baby Nursery",        icon: <BedDouble size={sz} />, image: '/icons/fashion/babybed.png' },
  { id: 25, name: "Maternity",           icon: <HeartHandshake size={sz} />, image: '/icons/fashion/breastfeeding.png' },
  { id: 26, name: "Baby Health & Safety", icon: <ShieldCheck size={sz} />, image: '/icons/fashion/rocker.png' },
  { id: 27, name: "Activity & Entertainment", icon: <ToyBrick size={sz} /> },
  { id: 28, name: "Baby Accessories",     icon: <Ribbon size={sz} /> },
  { id: 29, name: "Potty Training",       icon: <Bath size={sz} /> },
  { id: 30, name: "Gifts",                icon: <Gift size={sz} /> },
  { id: 31, name: "Baby Stationery",      icon: <Notebook size={sz} /> },
  { id: 32, name: "Car Seats & Accessories", icon: <CarFront size={sz} /> },
  { id: 33, name: "Clothing",             icon: <Shirt size={sz} /> },
  { id: 34, name: "Teething & Soothing",  icon: <Smile size={sz} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyClothing   (parentId: 16)
   ─────────────────────────────────────────────────────────── */
export const BabyClothing: CategoryItem[] = [
  { id: 1601, parentId: 16, name: "Onesies",    icon: <Baby size={16} /> },
  { id: 1602, parentId: 16, name: "Rompers",    icon: <Baby size={16} /> },
  { id: 1603, parentId: 16, name: "Baby Suits", icon: <Baby size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyShoes   (parentId: 17)
   ─────────────────────────────────────────────────────────── */
export const BabyShoes: CategoryItem[] = [
  { id: 1701, parentId: 17, name: "Baby Shoes", icon: <Footprints size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyCare   (parentId: 18)
   ─────────────────────────────────────────────────────────── */
export const BabyCare: CategoryItem[] = [
  { id: 1801, parentId: 18, name: "Bathing Basins & Seats", icon: <Droplets size={16} /> },
  { id: 1802, parentId: 18, name: "Baby Potty",             icon: <Circle size={16} /> },
  { id: 1803, parentId: 18, name: "Diapers",                icon: <Package size={16} /> },
  { id: 1804, parentId: 18, name: "Grooming Kits",          icon: <Scissors size={16} /> },
  { id: 1805, parentId: 18, name: "Teethers",                icon: <Smile size={16} /> },
  { id: 1806, parentId: 18, name: "Towels & Washcloths",    icon: <Layers size={16} /> },
  { id: 1807, parentId: 18, name: "Baby Lotion & Skincare", icon: <Sparkles size={16} /> },
  { id: 1808, parentId: 18, name: "Diaper Rash Cream",      icon: <Heart size={16} /> },
  { id: 1809, parentId: 18, name: "Nail Care Kits",          icon: <Scissors size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   MommyCare   (parentId: 19)
   ─────────────────────────────────────────────────────────── */
export const MommyCare: CategoryItem[] = [
  { id: 1901, parentId: 19, name: "Breast Pads",     icon: <Heart size={16} /> },
  { id: 1902, parentId: 19, name: "Feminine Care",   icon: <Heart size={16} /> },
  { id: 1903, parentId: 19, name: "Nursing Bras",    icon: <Heart size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyFeeding   (parentId: 20)
   ─────────────────────────────────────────────────────────── */
export const BabyFeeding: CategoryItem[] = [
  { id: 2001, parentId: 20, name: "Breast Pumps",       icon: <Droplets size={16} /> },
  { id: 2002, parentId: 20, name: "Breastfeeding",      icon: <Heart size={16} /> },
  { id: 2003, parentId: 20, name: "Feeding Bottles",    icon: <Milk size={16} /> },
  { id: 2004, parentId: 20, name: "Feeding Chairs",     icon: <Package size={16} /> },
  { id: 2005, parentId: 20, name: "Pacifiers",          icon: <Smile size={16} /> },
  { id: 2006, parentId: 20, name: "Weaning",            icon: <Leaf size={16} /> },
  { id: 2007, parentId: 20, name: "Sterilizers",        icon: <Droplets size={16} /> },
  { id: 2008, parentId: 20, name: "Bibs",               icon: <Layers size={16} /> },
  { id: 2009, parentId: 20, name: "High Chairs",        icon: <Armchair size={16} /> },
  { id: 2010, parentId: 20, name: "Baby Food & Formula", icon: <Package size={16} /> },
  { id: 2011, parentId: 20, name: "Sippy Cups",          icon: <Milk size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyBedtime   (parentId: 21)
   ─────────────────────────────────────────────────────────── */
export const BabyBedtime: CategoryItem[] = [
  { id: 2101, parentId: 21, name: "Cribs & Beddings", icon: <BedDouble size={16} /> },
  { id: 2102, parentId: 21, name: "Nests",             icon: <Feather size={16} /> },
  { id: 2103, parentId: 21, name: "Bassinets",         icon: <BedDouble size={16} /> },
  { id: 2104, parentId: 21, name: "Kids Furniture",    icon: <Package size={16} /> },
  { id: 2105, parentId: 21, name: "Pillows",           icon: <Square size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyTravel   (parentId: 22)
   ─────────────────────────────────────────────────────────── */
export const BabyTravel: CategoryItem[] = [
  { id: 2201, parentId: 22, name: "Baby Carriers", icon: <Package size={16} /> },
  { id: 2202, parentId: 22, name: "Diaper Bags",   icon: <BaggageClaim size={16} /> },
  { id: 2203, parentId: 22, name: "Strollers",     icon: <Package size={16} /> },
  { id: 2204, parentId: 22, name: "Car Seats",     icon: <ShieldCheck size={16} /> },
  { id: 2205, parentId: 22, name: "Travel Cots",   icon: <BedDouble size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyToys   (parentId: 23)
   ─────────────────────────────────────────────────────────── */
export const BabyToys: CategoryItem[] = [
  { id: 2301, parentId: 23, name: "Playmats",           icon: <Square size={16} /> },
  { id: 2302, parentId: 23, name: "Swings",              icon: <RefreshCw size={16} /> },
  { id: 2303, parentId: 23, name: "Rockers",             icon: <RefreshCw size={16} /> },
  { id: 2304, parentId: 23, name: "Walkers",             icon: <Footprints size={16} /> },
  { id: 2305, parentId: 23, name: "Soft Toys & Plush",   icon: <Heart size={16} /> },
  { id: 2306, parentId: 23, name: "Educational Toys",    icon: <Sparkles size={16} /> },
  { id: 2307, parentId: 23, name: "Bath Toys",           icon: <Droplets size={16} /> },
  { id: 2308, parentId: 23, name: "Ride-Ons",            icon: <ToyBrick size={16} /> },
  { id: 2309, parentId: 23, name: "Musical Toys",        icon: <Layers size={16} /> },
  { id: 2310, parentId: 23, name: "Books",               icon: <BookOpen size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyNursery   (parentId: 24)
   ─────────────────────────────────────────────────────────── */
export const BabyNursery: CategoryItem[] = [
  { id: 2401, parentId: 24, name: "Changing Tables",        icon: <Package size={16} /> },
  { id: 2402, parentId: 24, name: "Wardrobes & Storage",    icon: <Layers size={16} /> },
  { id: 2403, parentId: 24, name: "Nursery Decor",          icon: <Sparkles size={16} /> },
  { id: 2404, parentId: 24, name: "Mobiles & Night Lights", icon: <Moon size={16} /> },
  { id: 2405, parentId: 24, name: "Rugs",                    icon: <Square size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   Maternity   (parentId: 25)
   ─────────────────────────────────────────────────────────── */
export const Maternity: CategoryItem[] = [
  { id: 2501, parentId: 25, name: "Maternity Wear",              icon: <Shirt size={16} /> },
  { id: 2502, parentId: 25, name: "Maternity Support Belts",     icon: <HeartHandshake size={16} /> },
  { id: 2503, parentId: 25, name: "Nursing Wear",                icon: <Shirt size={16} /> },
  { id: 2504, parentId: 25, name: "Maternity Pillows",           icon: <Feather size={16} /> },
  { id: 2505, parentId: 25, name: "Postpartum Recovery",         icon: <Heart size={16} /> },
  { id: 2506, parentId: 25, name: "Pregnancy Tests & Ovulation", icon: <Droplets size={16} /> },
  { id: 2507, parentId: 25, name: "Stretch Mark Care",           icon: <Sparkles size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyHealthSafety   (parentId: 26)
   ─────────────────────────────────────────────────────────── */
export const BabyHealthSafety: CategoryItem[] = [
  { id: 2601, parentId: 26, name: "Baby Monitors",               icon: <Monitor size={16} /> },
  { id: 2602, parentId: 26, name: "Thermometers",                icon: <Thermometer size={16} /> },
  { id: 2603, parentId: 26, name: "Safety Gates",                icon: <ShieldCheck size={16} /> },
  { id: 2604, parentId: 26, name: "Socket & Corner Covers",      icon: <ShieldCheck size={16} /> },
  { id: 2605, parentId: 26, name: "First Aid Kits",              icon: <HeartHandshake size={16} /> },
  { id: 2606, parentId: 26, name: "Baby Vitamins & Supplements", icon: <Sparkles size={16} /> },
  { id: 2607, parentId: 26, name: "Play Yards & Playpens",       icon: <Square size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   ActivityEntertainment   (parentId: 27)
   ─────────────────────────────────────────────────────────── */
export const ActivityEntertainment: CategoryItem[] = [
  { id: 2701, parentId: 27, name: "Play Mats & Gyms",     icon: <Square size={16} /> },
  { id: 2702, parentId: 27, name: "Baby Swings",          icon: <RefreshCw size={16} /> },
  { id: 2703, parentId: 27, name: "Bouncers & Rockers",   icon: <RefreshCw size={16} /> },
  { id: 2704, parentId: 27, name: "Walkers & Jumpers",    icon: <Footprints size={16} /> },
  { id: 2705, parentId: 27, name: "Activity Centers",     icon: <ToyBrick size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyAccessories   (parentId: 28)
   ─────────────────────────────────────────────────────────── */
export const BabyAccessories: CategoryItem[] = [
  { id: 2801, parentId: 28, name: "Hats, Socks & Mittens", icon: <Ribbon size={16} /> },
  { id: 2802, parentId: 28, name: "Bibs",                  icon: <Layers size={16} /> },
  { id: 2803, parentId: 28, name: "Blankets & Swaddles",   icon: <Feather size={16} /> },
  { id: 2804, parentId: 28, name: "Baby Sunglasses",       icon: <Sparkles size={16} /> },
  { id: 2805, parentId: 28, name: "Bandanas & Bibs Sets",  icon: <Ribbon size={16} /> },
  { id: 2806, parentId: 28, name: "Baby Bags",              icon: <BaggageClaim size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   PottyTraining   (parentId: 29)
   ─────────────────────────────────────────────────────────── */
export const PottyTraining: CategoryItem[] = [
  { id: 2901, parentId: 29, name: "Potties & Seats",       icon: <Bath size={16} /> },
  { id: 2902, parentId: 29, name: "Training Pants",        icon: <Layers size={16} /> },
  { id: 2903, parentId: 29, name: "Step Stools",            icon: <Square size={16} /> },
  { id: 2904, parentId: 29, name: "Potty Training Seats",  icon: <Circle size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   Gifts   (parentId: 30)
   ─────────────────────────────────────────────────────────── */
export const Gifts: CategoryItem[] = [
  { id: 3001, parentId: 30, name: "Baby Gift Sets",   icon: <Gift size={16} /> },
  { id: 3002, parentId: 30, name: "Keepsakes",        icon: <Sparkles size={16} /> },
  { id: 3003, parentId: 30, name: "Personalized Gifts", icon: <Gift size={16} /> },
  { id: 3004, parentId: 30, name: "Milestone Blankets", icon: <Feather size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyStationery   (parentId: 31)
   ─────────────────────────────────────────────────────────── */
export const BabyStationery: CategoryItem[] = [
  { id: 3101, parentId: 31, name: "Baby Books",       icon: <BookOpen size={16} /> },
  { id: 3102, parentId: 31, name: "Milestone Cards",  icon: <Notebook size={16} /> },
  { id: 3103, parentId: 31, name: "Growth Charts",    icon: <GraduationCap size={16} /> },
  { id: 3104, parentId: 31, name: "Baby Journals",    icon: <Notebook size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   CarSeatsAccessories   (parentId: 32)
   ─────────────────────────────────────────────────────────── */
export const CarSeatsAccessories: CategoryItem[] = [
  { id: 3201, parentId: 32, name: "Infant Car Seats",       icon: <CarFront size={16} /> },
  { id: 3202, parentId: 32, name: "Convertible Car Seats",  icon: <CarFront size={16} /> },
  { id: 3203, parentId: 32, name: "Booster Seats",           icon: <CarFront size={16} /> },
  { id: 3204, parentId: 32, name: "Car Seat Accessories",    icon: <CarFront size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   Clothing   (parentId: 33)
   Full clothing range (mirrors what Fashion had), for the
   Kids audience within Baby & Maternity.
   ─────────────────────────────────────────────────────────── */
export const Clothing: CategoryItem[] = [
  { id: 3301, parentId: 33, name: "Onesies",         icon: <Baby size={16} /> },
  { id: 3302, parentId: 33, name: "Rompers",         icon: <Baby size={16} /> },
  { id: 3303, parentId: 33, name: "Bodysuits",       icon: <Baby size={16} /> },
  { id: 3304, parentId: 33, name: "School Uniforms", icon: <GraduationCap size={16} /> },
  { id: 3305, parentId: 33, name: "Dresses",         icon: <Heart size={16} /> },
  { id: 3306, parentId: 33, name: "Tops",            icon: <Shirt size={16} /> },
  { id: 3307, parentId: 33, name: "T-Shirts",        icon: <Shirt size={16} /> },
  { id: 3308, parentId: 33, name: "Vests",           icon: <Shirt size={16} /> },
  { id: 3309, parentId: 33, name: "Shirts",          icon: <Shirt size={16} /> },
  { id: 3310, parentId: 33, name: "Polo Shirts",     icon: <Shirt size={16} /> },
  { id: 3311, parentId: 33, name: "Knitwear",        icon: <Feather size={16} /> },
  { id: 3312, parentId: 33, name: "Sweaters",        icon: <Feather size={16} /> },
  { id: 3313, parentId: 33, name: "Hoodies",         icon: <Layers size={16} /> },
  { id: 3314, parentId: 33, name: "Sweatshirts",     icon: <Layers size={16} /> },
  { id: 3315, parentId: 33, name: "Jackets",         icon: <Feather size={16} /> },
  { id: 3316, parentId: 33, name: "Coats",           icon: <Feather size={16} /> },
  { id: 3317, parentId: 33, name: "Trousers",        icon: <Layers size={16} /> },
  { id: 3318, parentId: 33, name: "Denim",           icon: <Square size={16} /> },
  { id: 3319, parentId: 33, name: "Skirts",          icon: <Circle size={16} /> },
  { id: 3320, parentId: 33, name: "Shorts",          icon: <Scissors size={16} /> },
  { id: 3321, parentId: 33, name: "Tracksuits",      icon: <RefreshCw size={16} /> },
  { id: 3322, parentId: 33, name: "Activewear",      icon: <RefreshCw size={16} /> },
  { id: 3323, parentId: 33, name: "Jumpsuits",       icon: <Layers size={16} /> },
  { id: 3324, parentId: 33, name: "Playsuits",       icon: <Layers size={16} /> },
  { id: 3325, parentId: 33, name: "Loungewear",      icon: <Heart size={16} /> },
  { id: 3326, parentId: 33, name: "Pyjamas",         icon: <Moon size={16} /> },
  { id: 3327, parentId: 33, name: "Nightwear",       icon: <Moon size={16} /> },
  { id: 3328, parentId: 33, name: "Swimwear",        icon: <Droplets size={16} /> },
  { id: 3329, parentId: 33, name: "Beachwear",       icon: <Droplets size={16} /> },
  { id: 3330, parentId: 33, name: "Formal Wear",     icon: <Sparkles size={16} /> },
];

/* ───────────────────────────────────────────────────────────
   BabyTeething   (parentId: 34)
   Explicit teething & soothing export.
   ─────────────────────────────────────────────────────────── */
export const BabyTeething: CategoryItem[] = [
  { id: 3401, parentId: 34, name: "Teethers",              icon: <Smile size={16} /> },
  { id: 3402, parentId: 34, name: "Teething Rings",         icon: <Circle size={16} /> },
  { id: 3403, parentId: 34, name: "Teething Gels & Creams", icon: <Droplets size={16} /> },
  { id: 3404, parentId: 34, name: "Teething Toys",          icon: <ToyBrick size={16} /> },
  { id: 3405, parentId: 34, name: "Teething Necklaces",     icon: <Sparkles size={16} /> },
  { id: 3406, parentId: 34, name: "Soothers & Pacifiers",   icon: <Smile size={16} /> },
];

/* ═══════════════════════════════════════════════════════════
   AGGREGATED EXPORT (handy for lookups / mega menus)
═══════════════════════════════════════════════════════════ */
export const AllBabyMaternityProducts: CategoryItem[] = [
  ...BabyClothing,
  ...BabyShoes,
  ...BabyCare,
  ...MommyCare,
  ...BabyFeeding,
  ...BabyBedtime,
  ...BabyTravel,
  ...BabyToys,
  ...BabyNursery,
  ...Maternity,
  ...BabyHealthSafety,
  ...ActivityEntertainment,
  ...BabyAccessories,
  ...PottyTraining,
  ...Gifts,
  ...BabyStationery,
  ...CarSeatsAccessories,
  ...Clothing,
  ...BabyTeething,
];